// src/firebaseSync.js
import {
  collection,
  doc,
  setDoc,
  serverTimestamp,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

export const FIRESTORE_COLLECTIONS = {
  developerProjects: 'developer_projects',
  registrations: 'registrations',
  properties: 'properties',
  loginAttempts: 'login_attempts',
  propertyApprovals: 'property_approvals',
};

const sendToFirestore = async (collectionName, documentId, payload) => {
  try {
    if (!db) {
      return { ok: false, error: 'Firestore not initialised.' };
    }
    const docRef = doc(collection(db, collectionName), documentId);
    await setDoc(
      docRef,
      {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { ok: true, id: documentId };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Firestore ${collectionName} sync failed:`, error?.message || error);
    return { ok: false, error: error?.message || String(error) };
  }
};

// --- Login attempts (every login form submit) ---
export const sendLoginAttemptToFirestore = (auth) => {
  const documentId = `login-${Date.now()}`;
  return sendToFirestore(FIRESTORE_COLLECTIONS.loginAttempts, documentId, {
    ...auth,
    documentId,
    source: 'admin-login',
  });
};

// --- New registration (signup tab) ---
export const sendRegistrationToFirestore = (signup) => {
  const documentId = `reg-${Date.now()}`;
  return sendToFirestore(FIRESTORE_COLLECTIONS.registrations, documentId, {
    ...signup,
    documentId,
    status: 'pending-approval',
    source: 'admin-signup',
  });
};

// --- Property approvals (admin requests approval for a developer project) ---
export const sendPropertyApprovalToFirestore = (project) => {
  const documentId = `approval-${project.id || Date.now()}`;
  return sendToFirestore(FIRESTORE_COLLECTIONS.propertyApprovals, documentId, {
    projectId: project.id,
    projectName: project.name,
    location: project.location,
    rera: project.rera,
    requestedBy: project.owner || 'developer',
    panelStatus: 'requested',
    documentId,
    source: 'admin-project-approval',
  });
};

// --- Developer panel (top-of-shell profile / project desk) ---
export const sendDeveloperPanelToFirestore = (panel) => {
  const documentId = panel.developerId || `dev-panel-${Date.now()}`;
  return sendToFirestore(FIRESTORE_COLLECTIONS.developerProjects, documentId, {
    ...panel,
    documentId,
    source: 'admin-developer-panel',
  });
};

// --- Employee panel (verification / KYC / lead status updates) ---
export const sendEmployeePanelToFirestore = (panel) => {
  const documentId = panel.employeeId || `emp-panel-${Date.now()}`;
  return sendToFirestore('employee_panels', documentId, {
    ...panel,
    documentId,
    source: 'admin-employee-panel',
  });
};

// --- Existing flows (kept for backward compatibility) ---
export const sendDeveloperProjectToFirestore = (project) => {
  const documentId = project.id || `dev-project-${Date.now()}`;
  return sendToFirestore(FIRESTORE_COLLECTIONS.developerProjects, documentId, {
    ...project,
    documentId,
    source: 'admin-developer-workspace',
  });
};

// --- Approval queue subscription (admin Registrations panel) ---
export const subscribeToRegistrations = (callback) => {
  if (!db) {
    return () => {};
  }
  const registrationsRef = collection(db, FIRESTORE_COLLECTIONS.registrations);
  const q = query(registrationsRef, orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.warn('Registrations subscription failed:', error?.message || error);
      callback([]);
    }
  );
};

// --- Live dashboard metrics (count each collection) ---
export const fetchDashboardMetrics = async () => {
  if (!db) {
    return { properties: 0, featured: 0, developerProjects: 0, registrations: 0 };
  }
  try {
    const [propertiesSnap, devSnap, regSnap] = await Promise.all([
      getDocs(collection(db, FIRESTORE_COLLECTIONS.properties)),
      getDocs(collection(db, FIRESTORE_COLLECTIONS.developerProjects)),
      getDocs(collection(db, FIRESTORE_COLLECTIONS.registrations)),
    ]);
    const properties = propertiesSnap.docs.map((d) => d.data());
    return {
      properties: properties.length,
      featured: properties.filter((p) => p.featured === 'Yes').length,
      developerProjects: devSnap.size,
      registrations: regSnap.size,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Dashboard metrics fetch failed:', error?.message || error);
    return { properties: 0, featured: 0, developerProjects: 0, registrations: 0 };
  }
};

// ============================================================
// Public user auth (signup / login / OTP) — Firestore backed.
// ============================================================
//
// Storage layout:
//   users/{uid}                       : profile document
//   users/{uid}/otps/{otpId}          : one-time OTP (code, type, expiresAt, used)
//   users/{uid}/wishlist/{itemId}     : per-user wishlist items
//
// We don't use Firebase Authentication — this is a demo OTP flow with
// the code shown in the UI. To switch to real SMS later, only
// sendOtpToFirestore / verifyOtpAgainstFirestore need to be replaced.

const generateOtp = () => Math.floor(100000 + Math.random() * 900000).toString();
const OTP_TTL_MS = 10 * 60 * 1000; // 10 minutes

const usersCollection = () => collection(db, 'users');

export const generateUserId = () => `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// --- Send a 6-digit OTP and store it under the user's otps sub-collection ---
export const sendOtpToFirestore = async ({ uid, type }) => {
  try {
    if (!db) {
      return { ok: false, error: 'Firestore not initialised.' };
    }
    if (!uid || !type) {
      return { ok: false, error: 'uid and type are required.' };
    }
    const code = generateOtp();
    const otpId = `otp-${Date.now()}`;
    const expiresAt = Timestamp.fromMillis(Date.now() + OTP_TTL_MS);
    const otpRef = doc(collection(db, 'users', uid, 'otps'), otpId);
    await setDoc(otpRef, {
      code,
      type,
      used: false,
      createdAt: serverTimestamp(),
      expiresAt,
    });
    return { ok: true, code, otpId, expiresAt: expiresAt.toMillis() };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Firestore OTP send failed:', error?.message || error);
    return { ok: false, error: error?.message || String(error) };
  }
};

// --- Verify an OTP against Firestore (single-use, expiry-checked) ---
export const verifyOtpAgainstFirestore = async ({ uid, type, code }) => {
  try {
    if (!db || !uid || !type || !code) {
      return { ok: false, error: 'Missing required fields.' };
    }
    const otpsRef = collection(db, 'users', uid, 'otps');
    const q = query(otpsRef, where('type', '==', type), where('used', '==', false), orderBy('createdAt', 'desc'), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) {
      return { ok: false, error: 'No active OTP. Please resend.' };
    }
    const otpDoc = snap.docs[0];
    const data = otpDoc.data();
    if (data.code !== code) {
      return { ok: false, error: 'Invalid OTP code.' };
    }
    const expiresAtMs = data.expiresAt?.toMillis ? data.expiresAt.toMillis() : 0;
    if (Date.now() > expiresAtMs) {
      return { ok: false, error: 'OTP expired. Please request a new one.' };
    }
    await setDoc(otpDoc.ref, { used: true, usedAt: serverTimestamp() }, { merge: true });
    return { ok: true };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Firestore OTP verify failed:', error?.message || error);
    return { ok: false, error: error?.message || String(error) };
  }
};

// --- Create or update a user profile document ---
export const upsertUserProfile = async (user) => {
  try {
    if (!db || !user?.uid) {
      return { ok: false, error: 'Missing uid.' };
    }
    const userRef = doc(usersCollection(), user.uid);
    await setDoc(
      userRef,
      {
        uid: user.uid,
        fullName: user.fullName || '',
        mobile: user.mobile || '',
        email: user.email || '',
        mobileVerified: Boolean(user.mobileVerified),
        emailVerified: Boolean(user.emailVerified),
        requestedRole: user.requestedRole || 'user',
        role: user.role || 'user',
        ...(user.approvalStatus ? { approvalStatus: user.approvalStatus } : {}),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { ok: true, id: user.uid };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Firestore user upsert failed:', error?.message || error);
    return { ok: false, error: error?.message || String(error) };
  }
};

// --- Find an existing user by mobile (used during login) ---
export const findUserByMobile = async (mobile) => {
  try {
    if (!db || !mobile) {
      return { ok: false, error: 'Mobile is required.', user: null };
    }
    const q = query(usersCollection(), where('mobile', '==', mobile), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) {
      return { ok: true, user: null };
    }
    const docSnap = snap.docs[0];
    return { ok: true, user: { id: docSnap.id, ...docSnap.data() } };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Firestore user lookup failed:', error?.message || error);
    return { ok: false, error: error?.message || String(error), user: null };
  }
};

// --- Live subscription to a user's profile (for header badge auto-update) ---
export const subscribeToUserProfile = (uid, callback) => {
  if (!db || !uid) {
    return () => {};
  }
  const userRef = doc(usersCollection(), uid);
  return onSnapshot(
    userRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      callback({ id: snapshot.id, ...snapshot.data() });
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.warn('User profile subscription failed:', error?.message || error);
      callback(null);
    }
  );
};

// --- Per-user wishlist (sub-collection) ---
export const writeUserWishlistItem = async (uid, item) => {
  try {
    if (!db || !uid || !item) {
      return { ok: false, error: 'Missing uid or item.' };
    }
    const itemId = item.id || `wish-${Date.now()}`;
    const itemRef = doc(collection(db, 'users', uid, 'wishlist'), itemId);
    await setDoc(
      itemRef,
      {
        ...item,
        id: itemId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { ok: true, id: itemId };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Firestore wishlist write failed:', error?.message || error);
    return { ok: false, error: error?.message || String(error) };
  }
};

export const subscribeToUserWishlist = (uid, callback) => {
  if (!db || !uid) {
    callback([]);
    return () => {};
  }
  const wishlistRef = collection(db, 'users', uid, 'wishlist');
  return onSnapshot(
    wishlistRef,
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.warn('User wishlist subscription failed:', error?.message || error);
      callback([]);
    }
  );
};

